import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Award } from 'lucide-react';
import { locations, mapImage } from '@/data/locations';
import { useResponsive, getResponsiveTextClasses, getResponsiveSpacingClasses, getFocusClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import type { LocationInfo } from '@/types';

interface LocationAccordionProps {
  className?: string;
}

interface LocationItemProps {
  location: LocationInfo;
}

const LocationItem: React.FC<LocationItemProps> = ({ location }) => {
  const { isMobile } = useResponsive();
  
  return (
    <AccordionItem value={location.id} className="border-b border-gray-200">
      <AccordionTrigger className={`text-left hover:no-underline ${getResponsiveSpacingClasses('py', 4, 5, 6)} ${getResponsiveSpacingClasses('px', 4, 5, 6)} hover:bg-gray-50 transition-colors duration-200 ${getFocusClasses()}`}>
        <div className="flex items-center space-x-3">
          <MapPin className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-orange-600 flex-shrink-0`} />
          <div>
            <h3 className={`${getResponsiveTextClasses('text-base', 'text-lg', 'text-lg')} font-semibold text-gray-900`}>{location.name}</h3>
            <p className={`${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} text-gray-600 mt-1`}>{location.description}</p>
          </div>
        </div>
      </AccordionTrigger>
      
      <AccordionContent className={`${getResponsiveSpacingClasses('px', 4, 5, 6)} ${getResponsiveSpacingClasses('pb', 4, 5, 6)}`}>
        <div className="space-y-3 sm:space-y-4">
          {/* Address */}
          <div className="flex items-start space-x-3">
            <MapPin className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500 mt-1 flex-shrink-0`} />
            <div>
              <h4 className={`font-medium text-gray-900 mb-1 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>Dirección</h4>
              <p className={`text-gray-700 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>{location.address}</p>
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="flex items-start space-x-3">
            <Phone className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500 mt-1 flex-shrink-0`} />
            <div>
              <h4 className={`font-medium text-gray-900 mb-1 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>Teléfonos</h4>
              <div className="space-y-1">
                {location.phone.map((phone, index) => (
                  <p key={index} className={`text-gray-700 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
                    <a 
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className={`hover:text-orange-600 transition-colors duration-200 ${getFocusClasses()}`}
                    >
                      {phone}
                    </a>
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Certifications */}
          {location.certifications && location.certifications.length > 0 && (
            <div className="flex items-start space-x-3">
              <Award className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500 mt-1 flex-shrink-0`} />
              <div>
                <h4 className={`font-medium text-gray-900 mb-1 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>Certificaciones</h4>
                <div className="flex flex-wrap gap-2">
                  {location.certifications.map((cert, index) => (
                    <span
                      key={index}
                      className={`inline-block bg-orange-100 text-orange-800 font-medium ${getResponsiveTextClasses('text-xs', 'text-xs', 'text-xs')} ${getResponsiveSpacingClasses('px', 2, 2.5, 2.5)} ${getResponsiveSpacingClasses('py', 0.5, 0.5, 0.5)} rounded-full`}
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const LocationAccordion: React.FC<LocationAccordionProps> = ({ className = '' }) => {
  const { isMobile } = useResponsive();
  
  return (
    <section className={`${getResponsiveSpacingClasses('py', 12, 16, 20)} bg-white ${getTouchAccessibilityClasses()} ${className}`}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center ${getResponsiveSpacingClasses('mb', 8, 10, 12)}`}>
          <h2 className={getResponsiveTextClasses(
            'text-2xl font-bold text-gray-900 mb-4',
            'text-3xl',
            'text-4xl'
          )}>
            Nuestras Ubicaciones
          </h2>
          <p className={`${getResponsiveTextClasses('text-base', 'text-lg', 'text-xl')} text-gray-600 max-w-2xl mx-auto`}>
            LIEC cuenta con presencia estratégica en las principales ciudades de México, 
            brindando servicios de ingeniería y control de calidad cerca de ti.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-7 lg:gap-8 items-start`}>
          {/* Map Image */}
          <div className="order-2 lg:order-1">
            <Card className="overflow-hidden shadow-lg">
              <div className={`relative ${isMobile ? 'h-80' : 'h-96'} lg:h-full min-h-[400px]`}>
                <img
                  src={mapImage}
                  alt="Mapa de ubicaciones LIEC en México"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className={`bg-white/90 backdrop-blur-sm rounded-lg ${getResponsiveSpacingClasses('p', 3, 4, 4)}`}>
                    <h3 className={`font-semibold text-gray-900 mb-1 ${getResponsiveTextClasses('text-sm', 'text-base', 'text-base')}`}>
                      Cobertura Nacional
                    </h3>
                    <p className={`${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} text-gray-700`}>
                      {locations.length} oficinas estratégicamente ubicadas para brindar 
                      el mejor servicio en todo México.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Locations Accordion */}
          <div className="order-1 lg:order-2">
            <Card className="shadow-lg">
              <CardContent className="p-0">
                <Accordion type="single" collapsible className="w-full">
                  {locations.map((location) => (
                    <LocationItem key={location.id} location={location} />
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationAccordion;